import React, {useEffect, useRef, useState} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {H5, P} from '@modifi/ui'

import {HoverCardWrapper, MainContent, SlidingContent} from './styles'

const propTypes = {
  imageUrl: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  caption: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
}
const defaultProps = {}

export type HoverCardProps = InferPropTypes<typeof propTypes, typeof defaultProps>

export const HoverCard: React.FC<HoverCardProps> = ({imageUrl, headline, caption, description}) => {
  const [isHovered, setIsHovered] = useState(true)
  const [height, setHeight] = useState(0)
  const slidingContentRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (slidingContentRef.current) {
      setHeight(slidingContentRef.current.scrollHeight)
    }
  }, [])

  useEffect(() => {
    const targetElement = targetRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && targetElement) {
          setTimeout(() => setIsHovered(false), 1000)
          observer.unobserve(targetElement)
        }
      },
      {
        threshold: 0.5,
      }
    )

    if (targetElement) {
      observer.observe(targetElement)
    }

    return () => {
      if (targetElement) {
        observer.unobserve(targetElement)
      }
    }
  }, [])

  return (
    <HoverCardWrapper
      key={headline}
      lg={4}
      sm={6}
      mb={48}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="hover-card"
      ref={targetRef}
    >
      <MainContent isHovered={isHovered}>
        <img src={imageUrl} alt={headline} width={352} />
        <H5>{headline}</H5>
        <P mt={0}>{caption}</P>
      </MainContent>
      <SlidingContent ref={slidingContentRef} isHovered={isHovered} height={height}>
        <H5>{headline}</H5>
        <P mt={0}>{description}</P>
      </SlidingContent>
    </HoverCardWrapper>
  )
}

HoverCard.propTypes = propTypes

HoverCard.defaultProps = defaultProps

export default HoverCard
